import { ChangeEvent, Component } from "react";
import ReactCrop, { Crop } from "react-image-crop";
import Button from "components/ui/Button";
import { GhostButton } from "components/adminUi/Button";
import Dialog from "components/ui/Dialog";
import { Box } from "@mui/material";
import ApiErrorBanner from "components/ApiErrorBanner";

interface ImageCropperDialogProps {
  aspect: number;
  showDialog: boolean;
  onClose: () => void;
  onSubmitPreview: (args: { file: File; preview: string }) => void;
  onFileChange: (event: ChangeEvent<HTMLInputElement>) => void;
  file: File | any;
  selectedFileData?: string | null;
  type?: string | null;
}

interface ImageCropperDialogState {
  selectedFile: File | null;
  selectedFileData: string | null;
  preview: string | null;
  showSpinner: boolean;
  errors: string | object;
}

export class ImageCropperDialog extends Component<
  ImageCropperDialogProps,
  ImageCropperDialogState
> {
  state: ImageCropperDialogState = {
    selectedFile: null,
    selectedFileData: null,
    preview: null,
    showSpinner: false,
    errors: "",
  } as ImageCropperDialogState;
  cropper: Cropper | null = null;
  uploadFile = () => {
    if (this.props.file === null) {
      this.setState({ errors: "You must select a file." });
      return;
    }
    const croppedFile = this.cropper && this.cropper.getCroppedBlob();
    const blobFile = croppedFile ? croppedFile : this.props.file;
    let file = new File([blobFile], this.props.file.name, {
      type: this.props.type === "svg" ? "image/svg+xml" : "image/jpg",
    });

    this.props.onSubmitPreview({
      file: file,
      preview: this.state.preview!,
    });
  };

  updatePreview = (preview: string) => {
    this.setState({ preview });
  };

  handleErrors = (errors: string) => {
    this.setState({ errors: errors });
  };

  render() {
    return (
      <Dialog
        className="image-cropper-dialog"
        size="lg"
        open={this.props.showDialog}
        onClose={this.props.onClose}
        closeOnOverlayClick={false}
        title="Crop Image"
        secondaryAction={
          <GhostButton
            className="cancel"
            onClick={this.props.onClose}
            disabled={this.state.showSpinner}
          >
            Cancel
          </GhostButton>
        }
        mainAction={
          <Button
            size="small"
            variant="primary"
            className="submit"
            onClick={this.uploadFile}
            disabled={this.state.showSpinner}
          >
            Save
          </Button>
        }
        loading={this.state.showSpinner}
      >
        <ApiErrorBanner error={this.state.errors} />
        <input
          type="file"
          accept="image/jpg, image/jpeg, image/png, image/svg+xml"
          onChange={this.props.onFileChange}
        />
        {this.props.file !== null &&
          this.props.selectedFileData &&
          this.props.type !== "svg" && (
            <div style={{ minWidth: 500 }}>
              <Cropper
                src={this.props.selectedFileData}
                filename={this.props.file.name}
                updatePreview={this.updatePreview}
                handleErrors={this.handleErrors}
                aspect={this.props.aspect}
                ref={c => (this.cropper = c)}
              />
            </div>
          )}
      </Dialog>
    );
  }
}

interface CropperProps {
  src: string;
  updatePreview: (preview: string) => void;
  handleErrors: (errors: string) => void;
  filename: string;
  aspect: number;
}

interface CropperState {
  src: string;
  crop: Crop;
  blob: Blob | null;
  croppedImageUrl: string;
}

export class Cropper extends Component<CropperProps, CropperState> {
  state: CropperState = {
    src: this.props.src,
    crop: {
      unit: "%",
      width: 64,
      aspect: this.props.aspect,
    } as Crop,
    blob: null,
    croppedImageUrl: "",
  } as CropperState;
  imageRef: HTMLImageElement | null = null;
  fileUrl: string | null = null;

  componentDidUpdate(prevProps: CropperProps) {
    if (this.props.src !== prevProps.src) {
      this.setState({
        src: this.props.src,
      });
    }
  }

  // If you setState the crop in here you should return false.
  onImageLoaded = (image: HTMLImageElement) => {
    this.imageRef = image;
  };

  onCropComplete = (crop: Crop) => {
    this.makeClientCrop(crop);
  };

  onCropChange = (crop: Crop) => {
    this.setState({ crop: crop });
  };

  makeClientCrop = (crop: Crop) => {
    if (this.imageRef && crop.width && crop.height) {
      this.getCroppedImg(this.imageRef, crop)
        .then(({ fileUrl, blob }: { fileUrl: string; blob: Blob }) => {
          this.setState({ croppedImageUrl: fileUrl, blob: blob });
          this.props.updatePreview(fileUrl);
        })
        .catch((errors: string) => {
          this.props.handleErrors(errors);
        });
    }
  };

  getCroppedImg = (image: HTMLImageElement, crop: Crop) => {
    return new Promise<{ fileUrl: string; blob: Blob }>((resolve, reject) => {
      if (
        crop === undefined ||
        crop.width === undefined ||
        crop.height === undefined ||
        crop.x === undefined ||
        crop.y === undefined
      ) {
        reject("Something is wrong, please try again.");
        return;
      }
      let height = 600;
      let width = 600;
      if (this.props.aspect === 1) {
        height = 600;
        width = 600;
      } else if (this.props.aspect === 9 / 16) {
        height = 600;
        width = 400;
      } else if (this.props.aspect === 16 / 9) {
        height = 400;
        width = 600;
      }
      const canvas = document.createElement("canvas");
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;
      const ctx: any = canvas.getContext("2d");

      canvas.width = width;
      canvas.height = height;
      ctx.imageSmoothingQuality = "high";

      ctx.drawImage(
        image,
        crop.x * scaleX,
        crop.y * scaleY,
        crop.width * scaleX,
        crop.height * scaleY,
        0,
        0,
        width,
        height
      );
      canvas.toBlob(
        (blob: Blob | null) => {
          if (blob === null) {
            reject("Error occurred during cropping, try again.");
            return;
          }
          if (this.fileUrl !== null) {
            window.URL.revokeObjectURL(this.fileUrl);
          }
          // window.URL.revokeObjectURL(previewUrl);
          this.fileUrl = window.URL.createObjectURL(blob);
          resolve({
            fileUrl: this.fileUrl,
            blob: blob,
          });
        },
        "image/jpg",
        1
      );
    });
  };
  getCroppedBlob = () => {
    return this.state.blob;
  };

  render() {
    const { crop, src } = this.state;
    return (
      <>
        <ReactCrop
          src={src}
          crop={crop}
          onImageLoaded={this.onImageLoaded}
          onComplete={this.onCropComplete}
          onChange={this.onCropChange}
        />
        {this.state.croppedImageUrl && (
          <Box
            sx={{
              flexDirection: "column",
              display: "flex",
            }}
          >
            <span>Preview</span>
            <img
              src={this.state.croppedImageUrl}
              alt={"Preview"}
              style={{ width: "30%", height: "30%" }}
            />
          </Box>
        )}
      </>
    );
  }
}
